/**
 * A workspace's first screen (WALLIYO-0132).
 *
 * Nine departments, nine designs. This loads the one the department asks for
 * and hands it to the view that draws it — Executive is not a variation on
 * People, and pretending otherwise produced a page of numbers nobody could act
 * on.
 *
 * The server sends ids, never prose, so the same answer reads in eight
 * languages, and it picks *which* sentence is true because that depends on the
 * numbers. It also sends only what this reader could already open, so a Support
 * agent's page has no Payments figure on it rather than a hidden one.
 */

import type { ReactNode } from "react";

import type { Figure, OverviewHead, WorkspaceOverview } from "@wallyo/contracts/console/overview";
import type { DepartmentId } from "@wallyo/contracts/workspaces";

import { getWorkspaceOverview } from "../../api/console-api";
import { useApi } from "../../api/context";
import { t, type MessageId } from "../../i18n";
import { formatDate, formatNumber } from "../../i18n/format";
import { LoadFailed, Loading, useLoad } from "../config-parts";
import { AdminOverviewView } from "./AdminOverview";
import { ExecutiveOverviewView } from "./ExecutiveOverview";
import { FinanceOverviewView } from "./FinanceOverview";
import { GrowthOverviewView } from "./GrowthOverview";
import { peopleTabOf, PeopleOverviewView } from "./PeopleOverview";
import { ProductOverviewView } from "./ProductOverview";
import { SupportOverviewView } from "./SupportOverview";
import { TrustOverviewView } from "./TrustOverview";

const TONE_CLASS = {
  plain: "ov-figure",
  ok: "ov-figure ok",
  warn: "ov-figure warn",
  bad: "ov-figure bad",
} as const;

export const figureLabel = (id: string) => t(`overview.figure.${id}` as MessageId);

/** The row of figures every department but Executive opens with. */
export function Figures({ figures }: { figures: readonly Figure[] }) {
  if (figures.length === 0) return null;
  return (
    <div className="ov-figures">
      {figures.map((figure) => (
        <div key={figure.figureId} className={TONE_CLASS[figure.tone]}>
          <span className="figure">{figure.display}</span>
          <span className="foot">{figureLabel(figure.figureId)}</span>
        </div>
      ))}
    </div>
  );
}

/** A month, as its own name: "September". */
const monthName = (when: string) => formatDate(when, { month: "long" });

/** The line under the title: which day, which week, which month, or against which. */
function period({ kind, start }: OverviewHead["period"]): string {
  if (kind === "month") return monthName(start);
  if (kind === "againstPrevious") {
    const before = new Date(start);
    before.setUTCMonth(before.getUTCMonth() - 1);
    return t("overview.against", { month: monthName(before.toISOString().slice(0, 10)) });
  }
  if (kind === "week") {
    const end = new Date(start);
    end.setUTCDate(end.getUTCDate() + 6);
    // The month once, on the end: "19–25 September".
    return `${formatDate(start, { day: "numeric" })}–${formatDate(end.toISOString().slice(0, 10), {
      day: "numeric",
      month: "long",
    })}`;
  }
  return formatDate(start, { weekday: "long", day: "numeric", month: "long" });
}

/**
 * The title, the period and the one sentence worth reading.
 *
 * Not `PageTop`: every one of these pages leads with a sentence about today
 * rather than the name of the screen somebody just clicked, which is the point
 * of the design — the title is the answer, not the label.
 */
export function Head({ head, right }: { head: OverviewHead; right?: ReactNode }) {
  return (
    <header className="top ov-top">
      <div>
        <div className="ov-title">
          <h1>
            {t(`overview.title.${head.titleId}` as MessageId, {
              count: formatNumber(head.first),
              name: head.name ?? "",
              month: monthName(head.period.start),
            })}
          </h1>
          <span className="foot">{period(head.period)}</span>
        </div>
        {head.ledeId ? (
          <p className="ov-lede">
            {t(`overview.lede.${head.ledeId}` as MessageId, {
              first: formatNumber(head.first),
              second: formatNumber(head.second),
            })}
          </p>
        ) : null}
      </div>
      {right ? <div className="ov-head-links">{right}</div> : null}
    </header>
  );
}

export function OverviewScreen({ department, hash }: { department: DepartmentId; hash: string }) {
  const { client } = useApi();
  const load = useLoad<{ overview: WorkspaceOverview }>(() => getWorkspaceOverview(client, department), department);
  const overview = load.state.data?.overview;

  return (
    <>
      {overview ? (
        <Head head={overview.head} />
      ) : (
        <header className="top ov-top">
          <div>
            <div className="ov-title">
              <h1>{t("overview.sub")}</h1>
            </div>
          </div>
        </header>
      )}

      {load.state.status === "failed" && !overview ? (
        <LoadFailed
          message={t("overview.loadFailed")}
          retry={t("hr.retry")}
          error={load.state.error}
          onRetry={load.reload}
        />
      ) : null}
      {!overview && load.state.status === "loading" ? <Loading text={t("hr.loading")} /> : null}

      {overview ? (
        overview.empty ? (
          // Not a page of zeroes: zero reads as good news, and the truth is
          // this reader holds nothing this department reads.
          <div className="empty">{t("overview.nothingToShow")}</div>
        ) : overview.kind === "executive" ? (
          <ExecutiveOverviewView data={overview} />
        ) : overview.kind === "people" ? (
          <PeopleOverviewView data={overview} tab={peopleTabOf(hash, overview.tabs)} />
        ) : overview.kind === "support" ? (
          <SupportOverviewView data={overview} />
        ) : overview.kind === "trust" ? (
          <TrustOverviewView data={overview} />
        ) : overview.kind === "finance" ? (
          <FinanceOverviewView data={overview} />
        ) : overview.kind === "product" ? (
          <ProductOverviewView data={overview} />
        ) : overview.kind === "growth" ? (
          <GrowthOverviewView data={overview} />
        ) : overview.kind === "administration" ? (
          <AdminOverviewView data={overview} />
        ) : (
          <Figures figures={overview.figures} />
        )
      ) : null}
    </>
  );
}
